import { Alert, Group, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { recommenderPageLimitLevel, recommenderPageLimitReason } from '../../helpers/recommenderPageLimits';

const TITLES = {
  error: 'This page is over its product block limit',
  reached: 'This page has reached its product block limit',
  warning: 'This page is close to its product block limit',
};

const usageLabel = ({ current, limit }, noun) =>
  typeof limit === 'number' ? `${current} of ${limit} ${noun}` : `${current} ${noun}`;

const RecommenderPageUsage = ({ usage }) => {
  // The usage endpoint is informational, and the page still has to work when
  // the API has not served it (older deployment, failed read).
  if (!usage?.recommendersPerPage) {
    return null;
  }

  const level = recommenderPageLimitLevel(usage);
  const feeds = usage.distinctFeedsPerPage;
  const title =
    level === 'reached' && !recommenderPageLimitReason({ recommendersPerPage: usage.recommendersPerPage })
      ? 'This page has reached its product feed limit'
      : TITLES[level];

  return (
    <>
      <Group gap="md" mb="md">
        <Text size="sm" c="dimmed">
          {usageLabel(usage.recommendersPerPage, 'product blocks running')}
        </Text>
        {feeds?.applies && (
          <Text size="sm" c="dimmed">
            {usageLabel(feeds, 'distinct feeds')}
          </Text>
        )}
      </Group>
      {level && (
        <Alert
          color={level === 'error' ? 'red' : 'yellow'}
          icon={<IconAlertTriangle size={16} />}
          mb="md"
          title={title}
        >
          <Text size="sm">
            {level === 'warning'
              ? 'Once the limit is reached, no more product blocks can be enabled until some are turned off.'
              : 'The product blocks already running keep working and stay editable, but no more can be enabled until some are turned off.'}
          </Text>
          {usage.largeCatalog && typeof feeds?.limit === 'number' && (
            <Text size="sm" mt="xs">
              This is a large catalog ({usage.catalogSize?.toLocaleString()} products), so its pages are also limited
              to {feeds.limit} distinct product feeds.
            </Text>
          )}
          <Text size="sm" mt="xs">
            A super admin can raise these limits in the domain settings.
          </Text>
        </Alert>
      )}
    </>
  );
};

export default RecommenderPageUsage;
